import { useMemo } from 'react';

import type { BreakpointDeviceEnum, BreakpointEnum } from '@snowit/icetypes';

import useWindowDimensions from './useWindowDimensions';

// Breakpoints as constants
// ../uiStyle/mixins.scss
const BP = {
  sm: 768,
  md: 1024,
  lg: 1280,
  xl: 1440,
  xxl: 1920,
};

export type BreakpointMap = { [x in BreakpointEnum]?: boolean };
export type DeviceMap = { [x in BreakpointDeviceEnum]?: boolean };

export type BreakpointHookReturn = {
  breakpoints: BreakpointMap;
  minWidth: BreakpointMap;
  maxWidth: BreakpointMap;
  devices: DeviceMap;
  width: number;
  isDesktop: boolean;
};

const withinRange = (value: number, min: number, max: number): boolean =>
  value >= min && value < max;

export default function useBreakpoints(time = 150): BreakpointHookReturn {
  const { width } = useWindowDimensions(time);

  return useMemo((): BreakpointHookReturn => {
    const breakpoints: BreakpointMap = {
      xs: withinRange(width, 0, BP.sm),
      sm: withinRange(width, BP.sm, BP.md),
      md: withinRange(width, BP.md, BP.lg),
      lg: withinRange(width, BP.lg, BP.xl),
      xl: withinRange(width, BP.xl, BP.xxl),
      xxl: withinRange(width, BP.xxl, Number.POSITIVE_INFINITY),
    };

    const minWidth: BreakpointMap = {
      xs: true,
      sm: width >= BP.sm,
      md: width >= BP.md,
      lg: width >= BP.lg,
      xl: width >= BP.xl,
      xxl: width >= BP.xxl,
    };

    const maxWidth: BreakpointMap = {
      xs: width < BP.sm,
      sm: width < BP.md,
      md: width < BP.lg,
      lg: width < BP.xl,
      xl: width < BP.xxl,
      xxl: false,
    };

    const devices: DeviceMap = {
      desktop: minWidth.lg,
      tabletP: minWidth.md && !minWidth.lg,
      tabletL: minWidth.sm && !minWidth.md,
      mobile: minWidth.xs && !minWidth.sm,
    };

    // `isDesktop` tracks minWidth.sm, i.e. anything that is not mobile.
    return { devices, breakpoints, minWidth, maxWidth, width, isDesktop: !!minWidth.sm };
  }, [width]);
}
